import { useState, type FormEvent } from 'react';
import { toast } from '../components/toast';
import { get, post, put } from '../api';
import { Field, Problems } from '../components/Field';
import { describe } from '../errors';
import { useLoad } from '../hooks';

export interface ProviderOption {
  key: string;
  label: string;
  kind: 'text' | 'number' | 'select';
  choices?: { value: string; label: string }[];
  default?: string;
}

export interface ProviderInfo {
  id: string;
  label: string;
  secret_label: string;
  options: ProviderOption[];
}

interface EmailStatus {
  provider: string;
  providers: ProviderInfo[];
  options: Record<string, string>;
  from_email: string;
  from_name: string;
  reply_to: string;
  has_key: boolean;
  can_store_keys: boolean;
  recent: {
    id: number;
    to: string;
    kind: string;
    status: string;
    error: string | null;
    created_at: string;
  }[];
}

const KIND: Record<string, string> = {
  invite: 'Invite',
  password_reset: 'Password reset',
  test: 'Test',
};

/** How this business sends email: its own provider, or none. */
export function EmailSettings() {
  const [tick, setTick] = useState(0);
  const status = useLoad(() => get<EmailStatus>('/api/settings/email'), [tick]);
  const [choice, setChoice] = useState<string | null>(null);
  const [fromEmail, setFromEmail] = useState<string | null>(null);
  const [fromName, setFromName] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [options, setOptions] = useState<Record<string, string> | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [test, setTest] = useState('');

  const s = status.data;
  if (status.loading && !s) return null;
  if (status.error || !s || !Array.isArray(s.providers)) {
    // Email settings are not switched on for this server; nothing to show.
    return null;
  }

  const provider = choice ?? s.provider;
  const own = provider !== 'off';
  const info = s.providers.find((p) => p.id === provider);
  // What the form shows for each of the provider's own fields: edited, else stored, else its default.
  const optionValues: Record<string, string> = Object.fromEntries(
    (info?.options ?? []).map((o) => [
      o.key,
      options?.[o.key] ??
        (provider === s.provider ? s.options[o.key] : undefined) ??
        o.default ??
        '',
    ]),
  );

  async function save(e: FormEvent) {
    e.preventDefault();
    setProblems([]);
    setFields({});
    setTest('');
    try {
      await put('/api/settings/email', {
        provider,
        from_email: fromEmail ?? s!.from_email,
        from_name: fromName ?? s!.from_name,
        reply_to: replyTo ?? s!.reply_to,
        api_key: apiKey,
        options: optionValues,
      });
      setApiKey('');
      setOptions(null);
      toast('Saved.');
      setTick((t) => t + 1);
    } catch (err) {
      const d = describe(err);
      setProblems(d.problems);
      setFields(d.fields);
    }
  }

  async function sendTest() {
    setTest('');
    try {
      const r = await post<{ ok: boolean; to: string; error?: string }>('/api/settings/email/test');
      setTest(r.ok ? `A test email was sent to ${r.to}.` : `The test failed: ${r.error}`);
      setTick((t) => t + 1);
    } catch (err) {
      setTest(describe(err).problems[0] ?? 'The test failed.');
    }
  }

  return (
    <section className="stack" aria-labelledby="email-h">
      <h2 id="email-h">Email</h2>
      <p className="muted">
        How this business sends email: its own Resend account or its own mailbox. Choose which
        messages go out automatically under Messages below.
      </p>
      <Problems problems={problems} />
      <form onSubmit={save} className="stack" noValidate>
        <fieldset>
          <legend>Sending</legend>
          <Field label="Send email using" error={fields.provider}>
            <select
              value={provider}
              onChange={(e) => {
                setChoice(e.target.value);
                setOptions(null);
              }}
            >
              <option value="off">Do not send email</option>
              {s.providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </Field>
          {provider === 'off' && (
            <p className="muted">
              Nothing is emailed. When you invite someone, Teknika CRM shows you their sign-in link
              to pass on yourself. People cannot reset their own password by email.
            </p>
          )}
          {own && info && (
            <>
              {info.options.map((o) => (
                <Field key={o.key} label={o.label} error={fields[o.key]}>
                  {o.kind === 'select' ? (
                    <select
                      value={optionValues[o.key]}
                      onChange={(e) => setOptions({ ...optionValues, [o.key]: e.target.value })}
                    >
                      {(o.choices ?? []).map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={o.kind === 'number' ? 'number' : 'text'}
                      inputMode={o.kind === 'number' ? 'numeric' : undefined}
                      autoComplete="off"
                      value={optionValues[o.key]}
                      onChange={(e) => setOptions({ ...optionValues, [o.key]: e.target.value })}
                    />
                  )}
                </Field>
              ))}
              <Field label="From address" error={fields.from_email}>
                <input
                  type="email"
                  value={fromEmail ?? s.from_email}
                  onChange={(e) => setFromEmail(e.target.value)}
                />
              </Field>
              <Field label="From name (optional)">
                <input
                  value={fromName ?? s.from_name}
                  onChange={(e) => setFromName(e.target.value)}
                />
              </Field>
              <Field label="Reply-to address (optional)" error={fields.reply_to}>
                <input
                  type="email"
                  value={replyTo ?? s.reply_to}
                  onChange={(e) => setReplyTo(e.target.value)}
                />
              </Field>
              <Field
                label={
                  s.has_key && provider === s.provider
                    ? `${info.secret_label} (leave blank to keep)`
                    : info.secret_label
                }
                error={fields.api_key}
              >
                <input
                  type="password"
                  autoComplete="off"
                  value={apiKey}
                  disabled={!s.can_store_keys}
                  onChange={(e) => setApiKey(e.target.value)}
                />
              </Field>
              {!s.can_store_keys && (
                <p className="muted">This server is not set up to store keys yet.</p>
              )}
            </>
          )}
        </fieldset>
        <div className="row">
          <button type="submit">Save email settings</button>
          {provider !== 'off' && (
            <button type="button" className="quiet" onClick={() => void sendTest()}>
              Send me a test email
            </button>
          )}
        </div>
        {test && <p role="status">{test}</p>}
      </form>

      {s.recent.length > 0 && (
        <>
          <h3>Recent emails</h3>
          <table>
            <thead>
              <tr>
                <th>To</th>
                <th>What</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {s.recent.map((r) => (
                <tr key={r.id}>
                  <td>{r.to}</td>
                  <td>{KIND[r.kind] ?? r.kind}</td>
                  <td>
                    {r.status === 'sent'
                      ? 'Sent'
                      : r.status === 'skipped'
                        ? 'Not sent (email is off)'
                        : `Failed: ${r.error ?? ''}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
